import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Radio,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  Loader2,
  DatabaseZap,
  Mail,
  CreditCard,
  BellRing,
  UserCheck,
  Activity,
  ShieldAlert,
  ListTree,
} from "lucide-react";
import {
  EventLogResponseDTO,
  EventLogSearchRequestDTO,
  EventLogStatsDTO,
  EVENT_CATEGORIES,
  EVENT_STATUSES,
  EVENT_SEVERITIES,
} from "../Types/EventLogs";
import { searchEventLogs, getEventLogStats } from "../Services/EventLogService";
import { useEventLogsStream } from "../../Shared/Hooks/useEventLogsStream";
import { formatDateTime } from "../../Shared/Utils/HelperUtils";
import Pagination from "../../Shared/Pagination/Pagination";

interface Filters {
  text: string;
  category: string;
  status: string;
  severity: string;
  eventType: string;
  entityId: string;
  orderId: string;
  fromDate: string;
  toDate: string;
}

const EMPTY_FILTERS: Filters = {
  text: "",
  category: "",
  status: "",
  severity: "",
  eventType: "",
  entityId: "",
  orderId: "",
  fromDate: "",
  toDate: "",
};

const TAIL_CAP = 250;

const categoryIcon: Record<string, React.ReactNode> = {
  PAYMENT: <CreditCard size={13} />,
  EMAIL: <Mail size={13} />,
  NOTIFICATION: <BellRing size={13} />,
  AUTH: <UserCheck size={13} />,
  AUDIT: <ListTree size={13} />,
  ERROR: <ShieldAlert size={13} />,
  SYSTEM: <Activity size={13} />,
  API: <DatabaseZap size={13} />,
};

const severityStyles: Record<string, string> = {
  ERROR: "bg-red-500/10 text-red-400 border-red-500/30",
  WARN: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  INFO: "bg-sky-500/10 text-sky-400 border-sky-500/30",
};

const statusStyles: Record<string, string> = {
  SUCCESS: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  FAILED: "bg-red-500/10 text-red-400 border-red-500/30",
  PARTIAL_SUCCESS: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  WARN: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  SKIPPED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  PENDING: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  CANCELLED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const categoryStyles: Record<string, string> = {
  PAYMENT: "text-violet-400 border-violet-500/30 bg-violet-500/10",
  EMAIL: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  NOTIFICATION: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  AUTH: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
  AUDIT: "text-slate-300 border-slate-500/30 bg-slate-500/10",
  ERROR: "text-red-400 border-red-500/30 bg-red-500/10",
  SYSTEM: "text-fuchsia-400 border-fuchsia-500/30 bg-fuchsia-500/10",
  API: "text-blue-400 border-blue-500/30 bg-blue-500/10",
};

const statusIcon = (status: string) => {
  if (status === "SUCCESS") return <CheckCircle2 size={13} />;
  if (status === "FAILED") return <XCircle size={13} />;
  if (status === "WARN" || status === "PARTIAL_SUCCESS") return <AlertTriangle size={13} />;
  return <Info size={13} />;
};

const selectClass =
  "w-full bg-[#0f172a] border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:border-blue-500 outline-none";
const inputClass =
  "w-full bg-[#0f172a] border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:border-blue-500 outline-none";

const LogViewerComponentForEventLogs: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [logsData, setLogsData] = useState<EventLogResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [stats, setStats] = useState<EventLogStatsDTO>({
    total: 0,
    failures: 0,
    errors: 0,
    byCategory: {},
    byStatus: {},
  });
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
    hasNext: false,
    hasPrevious: false,
  });

  const liveIds = useRef<Set<string>>(new Set());

  const buildPayload = useCallback((): EventLogSearchRequestDTO => {
    const payload: EventLogSearchRequestDTO = {};

    const hasDate = filters.fromDate || filters.toDate;

    payload.category = filters.category || undefined;
    payload.status = filters.status || undefined;
    payload.severity = filters.severity || undefined;
    payload.eventType = filters.eventType || undefined;
    payload.entityId = filters.entityId || undefined;
    payload.text = filters.text || undefined;
    payload.orderId = filters.orderId || undefined;
    payload.fromDate =
      hasDate && filters.fromDate ? new Date(filters.fromDate).toISOString() : undefined;
    payload.toDate =
      hasDate && filters.toDate ? new Date(filters.toDate).toISOString() : undefined;

    return Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined && v !== "")
    ) as EventLogSearchRequestDTO;
  }, [filters]);

  const runSearch = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      const response = await searchEventLogs(buildPayload(), targetPage, 50);
      setLoading(false);

      if (response.success && response.data) {
        setLogsData(response.data.content);
        setPage(response.data.page);
        setPagination({
          page: response.data.page,
          size: response.data.size,
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          first: response.data.first,
          last: response.data.last,
          hasNext: response.data.hasNext,
          hasPrevious: response.data.hasPrevious,
        });
      }
    },
    [buildPayload]
  );

  const refreshStats = useCallback(async () => {
    const from = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const response = await getEventLogStats(from);
    if (response.success && response.data) {
      setStats(response.data);
    }
  }, []);

  const matchesFilters = useCallback(
    (e: EventLogResponseDTO): boolean => {
      const f = filters;
      if (f.category && e.category !== f.category) return false;
      if (f.status && e.status !== f.status) return false;
      if (f.severity && e.severity !== f.severity) return false;
      if (f.eventType && !e.eventType?.toLowerCase().includes(f.eventType.toLowerCase()))
        return false;
      if (f.entityId && !e.entityId?.toLowerCase().includes(f.entityId.toLowerCase()))
        return false;
      if (f.orderId && !e.orderId?.toLowerCase().includes(f.orderId.toLowerCase()))
        return false;
      if (f.text) {
        const t = f.text.toLowerCase();
        const haystack = [
          e.message,
          e.eventType,
          e.details,
          e.errorMessage,
          e.errorCode,
          e.orderId,
          e.correlationId,
          e.subject,
          e.recipient,
          e.source,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(t)) return false;
      }
      return true;
    },
    [filters]
  );

  const handleLiveEvent = useCallback(
    (event: EventLogResponseDTO) => {
      if (!event.id || liveIds.current.has(event.id)) return;
      liveIds.current.add(event.id);

      refreshStats();

      if (!matchesFilters(event)) return;

      setLogsData((prev) => {
        const next = [event, ...prev].filter((x) => x.id !== event.id).slice(0, TAIL_CAP);
        return next;
      });
    },
    [matchesFilters, refreshStats]
  );

  const { connected } = useEventLogsStream(liveEnabled, handleLiveEvent);

  useEffect(() => {
    runSearch(0);
    refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!liveEnabled) return;
    const interval = setInterval(refreshStats, 20000);
    return () => clearInterval(interval);
  }, [liveEnabled, refreshStats]);

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    runSearch(0);
  };

  const statsCards = useMemo(
    () => [
      {
        label: "Events (24h)",
        value: stats.total.toLocaleString(),
        accent: "text-sky-400",
        icon: <Activity size={16} />,
      },
      {
        label: "Failures (24h)",
        value: stats.failures.toLocaleString(),
        accent: "text-red-400",
        icon: <XCircle size={16} />,
      },
      {
        label: "Errors (24h)",
        value: stats.errors.toLocaleString(),
        accent: "text-amber-400",
        icon: <AlertTriangle size={16} />,
      },
    ],
    [stats]
  );

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  const handlePageChange = (p: number | ((prev: number) => number)) => {
    if (typeof p === "number") {
      runSearch(p);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#0f172a] text-slate-300 font-sans">
      {/* ── Header / Live toggle / Search ── */}
      <div className="flex-none border-b border-slate-800 px-4 py-3 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setLiveEnabled((v) => !v)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold border transition-all ${
            liveEnabled
              ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
              : "bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200"
          }`}
        >
          <Radio size={14} className={liveEnabled ? "animate-pulse" : ""} />
          {liveEnabled ? "LIVE" : "Live stream off"}
          {connected && liveEnabled && (
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
          )}
        </button>

        <div className="flex-1 min-w-[220px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={15} />
          <input
            type="text"
            value={filters.text}
            onChange={(e) => setFilters({ ...filters, text: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && runSearch(0)}
            placeholder="Search message, event, order id, correlation id, recipient..."
            className="w-full bg-[#0f172a] border border-slate-700 rounded-md py-2 pl-9 pr-3 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </div>

        <button
          onClick={() => runSearch(0)}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-xs font-semibold"
        >
          Run
        </button>

        <button
          onClick={resetFilters}
          className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 hover:border-slate-600 text-slate-300 px-3 py-2 rounded-md text-xs font-semibold"
        >
          <RefreshCw size={13} /> Clear
        </button>

        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`md:hidden flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-all ${
            showFilters || activeFilterCount > 0
              ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
              : "bg-slate-800 border-slate-700 text-slate-300"
          }`}
        >
          <Filter size={13} /> Filters
          {activeFilterCount > 0 && (
            <span className="px-1.5 rounded-full bg-blue-600 text-white text-[10px]">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            size={13}
            className={`transition-transform ${showFilters ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* ── Stats strip ── */}
      <div className="flex-none border-b border-slate-800 px-4 py-2.5 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-[auto_auto_auto_1fr] gap-2 items-center">
        <div className="flex gap-3">
          {statsCards.map((card) => (
            <div
              key={card.label}
              className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5"
            >
              <span className={card.accent}>{card.icon}</span>
              <div className="leading-tight">
                <div className={`text-sm font-bold ${card.accent}`}>{card.value}</div>
                <div className="text-[9px] text-slate-500 uppercase tracking-wide">
                  {card.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5 lg:justify-end">
          {Object.entries(stats.byCategory)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => {
                  setFilters((f) => ({ ...f, category: f.category === cat ? "" : cat }));
                  runSearch(0);
                }}
                className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] font-semibold ${
                  cat === "ERROR"
                    ? "text-red-400 border-red-500/40 bg-red-500/10"
                    : "text-slate-300 border-slate-700 bg-slate-900 hover:border-slate-600"
                }`}
              >
                {categoryIcon[cat]}
                {cat}
                <span className="opacity-70">{count}</span>
              </button>
            ))}
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div
        className={`flex-none border-b border-slate-800 px-4 py-2.5 ${
          showFilters ? "grid" : "hidden"
        } md:grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2`}
      >
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Category</label>
          <select
            className={selectClass}
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          >
            <option value="">All</option>
            {EVENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Status</label>
          <select
            className={selectClass}
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All</option>
            {EVENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Severity</label>
          <select
            className={selectClass}
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          >
            <option value="">All</option>
            {EVENT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Event type</label>
          <input
            className={inputClass}
            value={filters.eventType}
            placeholder="e.g. PAYMENT_VERIF"
            onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Entity / User id</label>
          <input
            className={inputClass}
            value={filters.entityId}
            placeholder="payment / user id"
            onChange={(e) => setFilters({ ...filters, entityId: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Razorpay order id</label>
          <input
            className={inputClass}
            value={filters.orderId}
            placeholder="order_xxx"
            onChange={(e) => setFilters({ ...filters, orderId: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">From</label>
          <input
            type="datetime-local"
            className={inputClass}
            value={filters.fromDate}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">To</label>
          <input
            type="datetime-local"
            className={inputClass}
            value={filters.toDate}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
          />
        </div>
      </div>

      {/* ── Results ── */}
      <div className="flex-1 overflow-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-500">
            <Loader2 size={22} className="animate-spin mr-2" /> Loading events...
          </div>
        ) : logsData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-600">
            <Filter size={28} className="mr-2 opacity-40" />
            {activeFilterCount > 0 ? "No events match the current filters." : "No events recorded yet."}
          </div>
        ) : (
          <div className="p-3">
            {liveEnabled && (
              <div className="mb-2 flex items-center gap-2 text-[10px] text-emerald-400">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                Tail stream active — new matching events appear at the top.
              </div>
            )}

            {/* Desktop table */}
            <table className="hidden md:table w-full text-left border-collapse">
              <thead>
                <tr className="text-slate-500 text-[10px] uppercase border-b border-slate-800">
                  <th className="pb-2 pl-2 w-8"></th>
                  <th className="pb-2 font-semibold">Time</th>
                  <th className="pb-2 font-semibold">Severity</th>
                  <th className="pb-2 font-semibold">Category</th>
                  <th className="pb-2 font-semibold">Event</th>
                  <th className="pb-2 font-semibold text-center">Status</th>
                  <th className="pb-2 font-semibold">Source</th>
                  <th className="pb-2 font-semibold">Entity</th>
                  <th className="pb-2 font-semibold">Message</th>
                </tr>
              </thead>
              <tbody className="text-xs font-mono">
                {logsData.map((log) => (
                  <EventRow
                    key={log.id}
                    log={log}
                    isExpanded={expandedLog === log.id}
                    onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  />
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="md:hidden flex flex-col divide-y divide-slate-800">
              {logsData.map((log) => (
                <MobileEventCard
                  key={log.id}
                  log={log}
                  isExpanded={expandedLog === log.id}
                  onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                />
              ))}
            </div>

            {!liveEnabled && (
              <Pagination
                page={pagination.page}
                size={pagination.size || 50}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={handlePageChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EventRow: React.FC<{
  log: EventLogResponseDTO;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ log, isExpanded, onToggle }) => {
  const severity = log.severity || "INFO";
  const status = log.status || "SUCCESS";
  const category = log.category || "SYSTEM";

  const entity =
    [log.entityId && `${log.entityType || "id"}=${log.entityId}`, log.orderId && `order=${log.orderId}`]
      .filter(Boolean)
      .join(" ") || "-";

  return (
    <React.Fragment>
      <tr className="border-b border-slate-800/60 hover:bg-slate-800/20 cursor-pointer transition-colors">
        <td className="py-2 pl-2" onClick={onToggle}>
          {isExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-500" />}
        </td>
        <td className="py-2 text-slate-400 whitespace-nowrap" onClick={onToggle}>
          {formatDateTime(log.createdAt, "dateTimeWithSeconds")}
        </td>
        <td className="py-2" onClick={onToggle}>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${severityStyles[severity] || severityStyles.INFO}`}>
            {severity}
          </span>
        </td>
        <td className="py-2" onClick={onToggle}>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${categoryStyles[category] || categoryStyles.SYSTEM}`}>
            {categoryIcon[category]}
            {category}
          </span>
        </td>
        <td className="py-2 text-slate-200" onClick={onToggle}>
          {log.eventType}
        </td>
        <td className="py-2 text-center" onClick={onToggle}>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${statusStyles[status] || statusStyles.SKIPPED}`}>
            {statusIcon(status)}
            {status}
          </span>
        </td>
        <td className="py-2 text-slate-400" onClick={onToggle}>
          {log.source || "-"}
        </td>
        <td className="py-2 text-[10px] text-slate-500 max-w-[160px] truncate" onClick={onToggle} title={entity}>
          {entity}
        </td>
        <td className="py-2 text-slate-400 max-w-[300px] truncate" onClick={onToggle} title={log.message || ""}>
          {log.message || log.errorMessage || "-"}
        </td>
      </tr>

      {isExpanded && (
        <tr className="bg-slate-900/60">
          <td colSpan={9} className="px-4 py-3">
            <div className="grid gap-3 text-xs">
              {log.message && <Detail label="Message" value={log.message} mono />}
              {log.details && <Detail label="Details" value={log.details} mono large />}
              {log.errorMessage && <Detail label="Error" value={log.errorMessage} mono />}
              {log.errorCode && <Detail label="Error code" value={log.errorCode} mono />}
              {log.metadata && <Detail label="Metadata" value={log.metadata} mono large />}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-slate-400">
                <KV k="Correlation id" v={log.correlationId} />
                <KV k="Entity" v={log.entityId ? `${log.entityType} ${log.entityId}` : undefined} />
                <KV k="Actor" v={log.actorId ? `${log.actorId}${log.actorRole ? ` (${log.actorRole})` : ""}` : undefined} />
                <KV k="Company id" v={log.companyId} />
                <KV k="Provider" v={log.provider} />
                <KV k="Recipient" v={log.recipient} />
                <KV k="Subject" v={log.subject} />
                <KV k="Order id" v={log.orderId} />
                {log.httpMethod && (
                  <KV k="HTTP" v={`${log.httpMethod} ${log.httpPath} → ${log.httpStatus}`} />
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const MobileEventCard: React.FC<{
  log: EventLogResponseDTO;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ log, isExpanded, onToggle }) => {
  const severity = log.severity || "INFO";
  const status = log.status || "SUCCESS";
  const category = log.category || "SYSTEM";

  const entity =
    [log.entityId && `${log.entityType || "id"}=${log.entityId}`, log.orderId && `order=${log.orderId}`]
      .filter(Boolean)
      .join(" ") || "-";

  return (
    <div className="py-3 cursor-pointer" onClick={onToggle}>
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <span className="text-blue-400 text-[11px] font-mono">
          {formatDateTime(log.createdAt, "dateTimeWithSeconds")}
        </span>
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
            severityStyles[severity] || severityStyles.INFO
          }`}
        >
          {severity}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${
            categoryStyles[category] || categoryStyles.SYSTEM
          }`}
        >
          {categoryIcon[category]}
          {category}
        </span>
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${
            statusStyles[status] || statusStyles.SKIPPED
          }`}
        >
          {statusIcon(status)}
          {status}
        </span>
      </div>

      <p className="text-slate-200 text-xs font-mono mb-0.5 break-words">{log.eventType}</p>
      <p className="text-slate-400 text-xs break-words">{log.message || log.errorMessage || "-"}</p>
      <p className="text-[10px] text-slate-500 truncate mt-0.5">
        {log.source || "-"} · {entity}
      </p>

      {isExpanded && (
        <div
          className="mt-3 grid gap-3 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {log.message && <Detail label="Message" value={log.message} mono />}
          {log.details && <Detail label="Details" value={log.details} mono large />}
          {log.errorMessage && <Detail label="Error" value={log.errorMessage} mono />}
          {log.errorCode && <Detail label="Error code" value={log.errorCode} mono />}
          {log.metadata && <Detail label="Metadata" value={log.metadata} mono large />}
          <div className="grid grid-cols-2 gap-2 text-slate-400">
            <KV k="Correlation id" v={log.correlationId} />
            <KV k="Entity" v={log.entityId ? `${log.entityType} ${log.entityId}` : undefined} />
            <KV k="Actor" v={log.actorId ? `${log.actorId}${log.actorRole ? ` (${log.actorRole})` : ""}` : undefined} />
            <KV k="Company id" v={log.companyId} />
            <KV k="Provider" v={log.provider} />
            <KV k="Recipient" v={log.recipient} />
            <KV k="Subject" v={log.subject} />
            <KV k="Order id" v={log.orderId} />
            {log.httpMethod && (
              <KV k="HTTP" v={`${log.httpMethod} ${log.httpPath} → ${log.httpStatus}`} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Detail: React.FC<{ label: string; value: string; mono?: boolean; large?: boolean }> = ({
  label,
  value,
  mono,
  large,
}) => (
  <div>
    <div className="text-[9px] uppercase text-slate-500 mb-1">{label}</div>
    <div
      className={`bg-slate-950 border border-slate-800 rounded-md p-2 text-slate-300 whitespace-pre-wrap break-words ${mono ? "font-mono" : ""} ${
        large ? "max-h-52 overflow-auto" : ""
      }`}
    >
      {value}
    </div>
  </div>
);

const KV: React.FC<{ k: string; v?: string }> = ({ k, v }) => (
  <div className="min-w-0">
    <div className="text-[9px] uppercase text-slate-600">{k}</div>
    <div className="truncate text-slate-300">{v || "-"}</div>
  </div>
);

export default LogViewerComponentForEventLogs;